import { useState, useRef, useEffect } from 'react'
import { parseSlashCommand } from '@/utils/slashCommands'
import { quickRollInputValue } from '@/utils/quickRoll'
import { socketService } from '@/services/socket'
import { useVTTStore } from '@/store'
import type { ChatMessage } from '@/types'
import { Button } from '@arcanecircle/ui/grimoire/button'
import { IconButton } from '@arcanecircle/ui/grimoire/icon-button'
import { Input } from '@arcanecircle/ui/ui/input'

interface MessageInputProps {
  participants: Array<{
    userId: string
    displayName: string
    role: string
  }>
  currentUserId: string
}

/**
 * Chat composer. The leading dice IconButton is a quick-roll shortcut: it
 * prefills the input with the roll prefix (`/r `) and focuses it. The richer
 * quick-roll panel remains separate feature work.
 */
export function MessageInput({ participants, currentUserId }: MessageInputProps) {
  const [input, setInput] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const addMessage = useVTTStore((state) => state.addMessage)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Post a local-only system notice into the feed. Never hits the socket, so
  // only the sender sees it — used to surface whisper errors instead of
  // silently dropping the message.
  const postLocalNotice = (text: string) => {
    const notice: ChatMessage = {
      messageId: `local-${Date.now()}`,
      userId: 'system',
      displayName: 'System',
      message: text,
      timestamp: new Date().toISOString(),
      isWhisper: true,
    }
    addMessage(notice)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = input.trim()
    if (!trimmed) return

    const command = parseSlashCommand(trimmed, participants, currentUserId)

    try {
      switch (command.type) {
        case 'chat':
          socketService.sendChatMessage(command.content)
          break
        case 'whisper':
          if (!command.whisperTo || command.whisperTo.length === 0) {
            // No recipient resolved. Surface a visible error rather than
            // dropping the message, and keep the text in the box so the
            // user can fix the name instead of retyping the whole thing.
            const attempted = command.unresolvedTargets?.length
              ? command.unresolvedTargets.join(', ')
              : null
            postLocalNotice(
              attempted
                ? `No one here matches "${attempted}". Whisper not sent — check the name and try again.`
                : 'Whisper needs a recipient, e.g. /w Name your message. Nothing was sent.'
            )
            return
          }
          socketService.sendChatMessage(command.content, command.whisperTo)
          break
        case 'roll':
          if (command.notation) {
            socketService.rollDice(command.notation, undefined, command.whisperTo)
          }
          break
      }
      setInput('')
    } catch (error) {
      console.error('Failed to send message:', error)
    }
  }

  const handleQuickRoll = () => {
    setInput(quickRollInputValue())
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.375rem',
        padding: '0.5rem 0.625rem',
        borderTop: '1px solid hsl(var(--ac-dark-accent))',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <IconButton
          type="button"
          size="sm"
          title="Quick rolls"
          aria-label="Quick rolls"
          onClick={handleQuickRoll}
        >
          <svg
            width={14}
            height={14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="3" />
            <circle cx="8" cy="8" r="1.2" fill="currentColor" />
            <circle cx="16" cy="8" r="1.2" fill="currentColor" />
            <circle cx="8" cy="16" r="1.2" fill="currentColor" />
            <circle cx="16" cy="16" r="1.2" fill="currentColor" />
            <circle cx="12" cy="12" r="1.2" fill="currentColor" />
          </svg>
        </IconButton>
        <Input
          ref={inputRef}
          type="text"
          placeholder="/roll 1d20 +5 or just chat…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{ height: 36, fontSize: '0.8125rem' }}
        />
        <Button
          type="submit"
          size="sm"
          disabled={!input.trim()}
          style={{ height: 36 }}
        >
          Send
        </Button>
      </div>
      <div
        style={{
          fontSize: '0.625rem',
          color: 'hsl(var(--ac-text-muted))',
          paddingLeft: '0.25rem',
        }}
      >
        <span style={{ color: 'hsl(var(--ac-mint-primary))' }}>Tip:</span>{' '}
        <code className="font-mono-ac">/r 1d20+5</code> ·{' '}
        <code className="font-mono-ac">/w name ...</code> ·{' '}
        <code className="font-mono-ac">/gmroll 4d6</code>
      </div>
    </form>
  )
}
